/* @flow */
import React, { PureComponent } from 'react';
import * as Immutable from 'immutable';
import L from 'leaflet';
import classnames from 'classnames';
import { Map as LeafletMap, ZoomControl, LeafletConsumer } from 'react-leaflet';
import ReactLeafletMarker from 'common/components/core/leaflet/ReactLeafletMarker';
import {
    GoogleLayer,
    MapTypeEnum
} from 'common/components/core/leaflet/GoogleLayer';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import Zone from 'common/components/core/leaflet/Zone';
import LeafletPM from 'common/components/core/leaflet/LeafletPM';
import theme from './LeafletMapWithZones.scss';
import { ZoneMapGeometryStatusEnum } from 'common/enums/ZoneMapGeometryStatus';
import { ZoneMapGeometryTypeEnum } from 'common/enums/ZoneMapGeometryType';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';

import ZoneMapPlacesSearchBox from 'common/components/zoneMap/ZoneMapPlacesSearchBox';
import ZoneMapSearchMarker from 'common/components/zoneMap/ZoneMapSearchMarker';
import Control from 'react-leaflet-control';
import { ButtonTypes, ButtonWithPopover } from 'common/components/core/button';
import Icon from 'common/components/core/icon';
import testId from 'common/tests/masterManagement/mapMaster.json';
import { bulkVehicleFetch, vehicleModel } from 'common/middleware/api';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { MasterVehicle } from 'common/models/internal/MasterVehicle';
import { VehicleModel } from 'common/models/flow/Plan';
import type { ZoneMapGeometry } from 'common/models/internal/ZoneMapGeometry';

import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import { translate } from 'react-i18next';

type State = {
    leafletMap: ?L,
    zoom: number,
    firstLoad: boolean,
    workingLayer: ?LeafletMap,
    zoneBeingEdited: ?ZoneMapGeometry,
    availableVehicles: Immutable.List<MasterVehicle>,
    availableVehicleModels: Immutable.List<VehicleModel>,
    error: ?boolean
};
class LeafletMapWithZones extends PureComponent<$FlowFixMeProps, State> {
    static defaultProps = {
        defaultCountryCenter: [17.152357, 77.577157]
    };
    constructor(props) {
        super(props);
        this.state = {
            leafletMap: null,
            zoom: 13,
            firstLoad: true,
            workingLayer: null,
            zoneBeingEdited: null,
            availableVehicles: Immutable.List(),
            availableVehicleModels: Immutable.List(),
            error: false
        };
    }

    componentDidMount() {
        const {
            clientId,
            teamId,
            mapMetadataSettings,
            showToast,
            t
        } = this.props;
        if (mapMetadataSettings.showVehicles) {
            const teamsFilter = new FilterBuilder()
                .withName('teams')
                .withOperation(FilterOperation.Contains)
                .withValues({ clientId, teamId })
                .withCaseSensitive(true);
            const statusFilter = new FilterBuilder()
                .withName('status')
                .withOperation(FilterOperation.Equals)
                .withValues('ACTIVE');
            const filters = new FilterListBuilder()
                .addAll([teamsFilter, statusFilter])
                .build();
            bulkVehicleFetch
                .post({ clientId }, { filters })
                .then(res =>
                    Immutable.List(
                        res.vehicles.map(vehicle =>
                            MasterVehicle.fromJS(vehicle)
                        )
                    )
                )
                .then(availableVehicles =>
                    this.setState({
                        availableVehicles
                    })
                )
                .catch(() => {
                    showToast(
                        {
                            message: t('zoneMap.fetchVehicleError'),
                            type: ToastTypes.Error
                        },
                        { clientId }
                    );
                });
        }
        if (mapMetadataSettings.showVehicleModels) {
            vehicleModel
                .fetch({ clientId })
                .then(res =>
                    Immutable.List(
                        res.map(vehicleModel =>
                            VehicleModel.fromJS(vehicleModel)
                        )
                    )
                )
                .then(availableVehicleModels =>
                    this.setState({
                        availableVehicleModels
                    })
                )
                .catch(() => {
                    showToast(
                        {
                            message: t('zoneMap.fetchVehicleModelError'),
                            type: ToastTypes.Error
                        },
                        { clientId }
                    );
                });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { bounds, hoveredGeometryIds, geometries } = this.props;
        const { firstLoad, zoom, leafletMap, workingLayer } = this.state;
        const mapBounds = bounds && bounds.size ? bounds.toArray() : undefined;
        const maxZoom = bounds && bounds.size === 1 ? zoom : null;
        if (leafletMap && mapBounds && mapBounds.length && firstLoad) {
            this.setState({ firstLoad: false });
            leafletMap.fitBounds(mapBounds, { maxZoom });
        }

        if (
            !prevState.workingLayer &&
            workingLayer &&
            hoveredGeometryIds &&
            hoveredGeometryIds.length
        ) {
            const zoneIdBeingEdited =
                hoveredGeometryIds[hoveredGeometryIds.length - 1];
            const zoneBeingEdited = geometries.find(
                geometry => geometry.id.getId() === zoneIdBeingEdited
            );
            if (
                zoneBeingEdited &&
                zoneBeingEdited.getType() === ZoneMapGeometryTypeEnum.Polygon
            ) {
                this.setState({
                    zoneBeingEdited: zoneIdBeingEdited
                });
            }
        }
    }
    getZones = ({ geometries = [] }) => {
        const {
            leafletMap,
            zoneBeingEdited,
            availableVehicles,
            availableVehicleModels
        } = this.state;
        const {
            teamId,
            selectedZoneId,
            hoveredGeometryIds,
            setLayerRef,
            folders,
            handleZoneDelete,
            onUpdateGeometry,
            pincodes,
            bounds,
            handleZoneClick,
            handlePopupClose,
            handleGeometryHover,
            resetHoveredGeometries,
            namedZones,
            mapMetadataSettings
        } = this.props;
        return geometries.map(zone => {
            const { id: geometryId } = zone;
            const id = geometryId.getId();
            const internalStatus = zone.getInternalStatus();

            const isSelected = !zone.isHidden() && selectedZoneId === id;
            const isHovered = hoveredGeometryIds.includes(id);
            const type = zone.getType();
            const zoneBounds = bounds && bounds.get(id);
            const isZoneBeingEdited = id === zoneBeingEdited;
            const isEditing =
                leafletMap &&
                leafletMap.pm &&
                (leafletMap.pm.Draw.Polygon.enabled() ||
                    leafletMap.pm.Draw.Circle.enabled() ||
                    leafletMap.pm.Draw.Line.enabled());
            return type !== ZoneMapGeometryTypeEnum.Point &&
                internalStatus !== ZoneMapGeometryStatusEnum.Inactive &&
                !zone.isHidden() ? (
                <Zone
                    key={id}
                    teamId={teamId}
                    editMode={isZoneBeingEdited}
                    isEditing={isEditing}
                    namedZones={namedZones}
                    zone={zone}
                    zoneBounds={zoneBounds}
                    map={leafletMap}
                    type={type}
                    folders={folders}
                    onClick={handleZoneClick}
                    selectedZoneId={selectedZoneId}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    setLayerRef={setLayerRef}
                    handleZoneDelete={handleZoneDelete}
                    handlePopupClose={handlePopupClose}
                    onUpdateGeometry={onUpdateGeometry}
                    pincodes={pincodes}
                    enableMapEvents={this.enableMapEvents}
                    disableMapEvents={this.disableMapEvents}
                    handleGeometryHover={handleGeometryHover}
                    resetHoveredGeometries={resetHoveredGeometries}
                    availableVehicles={availableVehicles}
                    availableVehicleModels={availableVehicleModels}
                    mapMetadataSettings={mapMetadataSettings}
                />
            ) : null;
        });
    };

    fitToSearchMarkerBounds = layer => {
        layer &&
            this.state.leafletMap &&
            this.state.leafletMap.fitBounds(
                [layer.getLatLng(), layer.getLatLng()],
                { maxZoom: 13 }
            );
    };

    handleSearchMarkerDelete = id => {
        const { leafletMap } = this.state;
        const { bounds, handleZoneDelete } = this.props;

        leafletMap && bounds.size && leafletMap.fitBounds(bounds.toArray());
        id && handleZoneDelete(id);
    };

    disableScroll = () => {
        const { leafletMap } = this.state;
        if (leafletMap) {
            leafletMap.scrollWheelZoom && leafletMap.scrollWheelZoom.disable();
        }
    };

    enableScroll = () => {
        const { leafletMap } = this.state;
        if (leafletMap) {
            leafletMap.scrollWheelZoom && leafletMap.scrollWheelZoom.enable();
        }
    };

    enableMapEvents = () => {
        const { leafletMap } = this.state;
        if (leafletMap) {
            leafletMap.dragging && leafletMap.dragging.enable();
            leafletMap.touchZoom && leafletMap.touchZoom.enable();
            leafletMap.doubleClickZoom && leafletMap.doubleClickZoom.enable();
            leafletMap.boxZoom && leafletMap.boxZoom.enable();
            leafletMap.keyboard && leafletMap.keyboard.enable();
        }
    };

    disableMapEvents = () => {
        const { leafletMap } = this.state;
        if (leafletMap) {
            leafletMap.dragging && leafletMap.dragging.disable();
            leafletMap.touchZoom && leafletMap.touchZoom.disable();
            leafletMap.doubleClickZoom && leafletMap.doubleClickZoom.disable();
            leafletMap.boxZoom && leafletMap.boxZoom.disable();
            leafletMap.keyboard && leafletMap.keyboard.disable();
        }
    };

    getMapCenter = homebase => {
        return homebase && homebase.geometry && homebase.geometry.latLng
            ? homebase.geometry.latLng.flatten()
            : undefined;
    };

    getMarkers = ({ geometries = [], center }) => {
        let markers = geometries.map(geometry => {
            return geometry.getType() === ZoneMapGeometryTypeEnum.Point &&
                geometry.getInternalStatus() ===
                    ZoneMapGeometryStatusEnum.Search &&
                geometry.getInternalStatus() !==
                    ZoneMapGeometryStatusEnum.Inactive ? (
                <ZoneMapSearchMarker
                    geometry={geometry}
                    panTo={this.fitToSearchMarkerBounds}
                    onDelete={this.handleSearchMarkerDelete}
                />
            ) : null;
        });
        if (center) {
            const [lat, lng] = center;
            markers = markers.concat(
                <ReactLeafletMarker
                    position={{ lat, lng }}
                    iconSize={[36, 36]}
                    className={theme.homebaseWrap}
                >
                    <div testId={testId.map.homebase}>
                        <Icon
                            circular
                            center
                            theme={theme}
                            icon="homebase"
                            bgColor="cool-green"
                            iconColor="white"
                        />
                    </div>
                </ReactLeafletMarker>
            );
        }

        return markers;
    };

    getShapeType = shape => {
        switch (shape) {
            case 'Circle':
                return ZoneMapGeometryTypeEnum.Circle;
            case 'Line':
                return ZoneMapGeometryTypeEnum.Polyline;
            default:
                return ZoneMapGeometryTypeEnum.Polygon;
        }
    };

    onCreate = e => {
        const { zoneBeingEdited } = this.state;
        const { createZone, bounds } = this.props;
        const { layer, shape } = e;
        const type = this.getShapeType(shape);
        const parentId =
            zoneBeingEdited &&
            bounds.get(zoneBeingEdited).contains(layer.getBounds())
                ? zoneBeingEdited
                : null;
        createZone({ layer, type, parentId });
        this.setState({
            workingLayer: null,
            zoneBeingEdited: null
        });
    };

    handleClick = () => {
        const { leafletMap } = this.state;
        const { resetActiveZone, resetActiveGroup } = this.props;
        if (
            leafletMap &&
            (!leafletMap._popup ||
                (leafletMap._popup && !leafletMap._popup.isOpen()))
        ) {
            resetActiveZone();
            resetActiveGroup();
        }
    };

    removeLastVertex = mode => {
        const { leafletMap } = this.state;
        // remove last coords
        if (mode === 'Circle') {
            leafletMap.pm.Draw[mode].disable();
            return;
        }

        const coords = leafletMap.pm.Draw[mode]._layer.getLatLngs();
        const removedCoord = coords.pop();

        // if all coords are gone, cancel drawing
        if (coords.length < 1) {
            leafletMap.pm.Draw[mode].disable();
            this.setState({
                workingLayer: null,
                zoneBeingEdited: null,
                error: null
            });
            return;
        }

        // find corresponding marker
        const marker = leafletMap.pm.Draw[mode]._layerGroup
            .getLayers()
            .filter(l => l instanceof L.Marker)
            .filter(l => !L.DomUtil.hasClass(l._icon, 'cursor-marker'))
            .find(l => l.getLatLng() === removedCoord);

        // remove that marker
        leafletMap.pm.Draw[mode]._layerGroup.removeLayer(marker);

        // update layer with new coords
        leafletMap.pm.Draw[mode]._layer.setLatLngs(coords);

        // sync the hintline again
        leafletMap.pm.Draw[mode]._syncHintLine();
    };

    onDrawStart = e => {
        const { leafletMap } = this.state;
        const { workingLayer, shape } = e;

        leafletMap && leafletMap.closePopup();
        if (workingLayer && shape !== 'Circle') {
            workingLayer.on('pm:vertexadded', () => {
                const { error } = this.state;
                if (error) {
                    this.removeLastVertex(shape);
                } else {
                    this.setState({
                        workingLayer
                    });
                }
            });
        }
    };

    //A drawing of a zone in zone can error if mouse cursor:
    //  1. Outside the zone.
    //  2. Intersects with other zones inside parent
    checkIfDrawingErrored = () => {
        const { leafletMap, zoneBeingEdited } = this.state;
        const { layerRefs } = this.props;
        if (leafletMap && zoneBeingEdited) {
            const parent = layerRefs.get(zoneBeingEdited);
            const cursor = leafletMap.pm.Draw.Polygon._hintMarker;
            const cursorMarker = leafletMap.latLngToLayerPoint(
                cursor.getLatLng()
            );
            const isWithinParent = parent._containsPoint(cursorMarker);

            this.setState(prevState => {
                if (!prevState.error && !isWithinParent) {
                    return { error: true };
                }
                if (prevState.error && isWithinParent) {
                    return { error: false };
                }
                return;
            });
        }
    };

    onMapLoad = ref => {
        const map = ref && ref.leafletElement;
        map && map.on('mousemove', this.checkIfDrawingErrored);
        map && map.on('pm:create', this.onCreate);
        map && map.on('click', this.handleClick);
        map && map.on('pm:drawstart', this.onDrawStart);

        this.setState({ leafletMap: map });
    };

    getRadiusAndCenter = () => {
        const { leafletMap } = this.state;
        const bounds = leafletMap && leafletMap.getBounds();
        const center = bounds.getCenter();
        const ne = bounds.getNorthEast();
        const radius = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(center),
            new google.maps.LatLng(ne)
        );
        return { center, radius };
    };

    invalidateMapSize = () => {
        const { leafletMap } = this.state;
        if (leafletMap) {
            leafletMap.invalidateSize();
        }
    };

    handleSidebarToggle = () => {
        const { isExpanded, onCollapseSidebar, onExpandSidebar } = this.props;
        isExpanded
            ? onCollapseSidebar(this.invalidateMapSize)
            : onExpandSidebar(this.invalidateMapSize);
    };

    getCollapseButton = () => {
        const { isExpanded, t } = this.props;
        return (
            <Control position="bottomleft">
                <ButtonWithPopover
                    theme={theme}
                    onClick={this.handleSidebarToggle}
                    className={theme.collapseBtn}
                    icon={isExpanded ? 'caret_left' : 'caret_right'}
                    invertPopover
                    popOverContent={t('zoneMap.toggleSidebar', {
                        action: isExpanded
                            ? t('zoneMap.hide')
                            : t('zoneMap.show')
                    })}
                    place={PopoverPlaceEnum.Right}
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    buttonType={ButtonTypes.Solid}
                />
            </Control>
        );
    };

    render() {
        const { leafletMap, zoom, error, availableVehicles } = this.state;
        const {
            geometries,
            folders,
            handlePlaceSearch,
            mapCenter,
            t,
            homebase,
            availableRiders,
            defaultCountryCenter
        } = this.props;
        const homebaseLatLng = this.getMapCenter(homebase);
        const center = mapCenter || homebaseLatLng || defaultCountryCenter;
        return (
            <div
                className={classnames(theme.leafletMapContainer, {
                    [theme.hasErrors]: !!error
                })}
            >
                <LeafletMap
                    ref={this.onMapLoad}
                    center={center}
                    zoomControl={false}
                    zoom={zoom}
                    closePopupOnClick={false}
                >
                    <ZoomControl position="bottomright" />
                    <LeafletConsumer>
                        {context => (
                            <GoogleLayer
                                type={MapTypeEnum.Roadmap}
                                leaflet={context}
                            />
                        )}
                    </LeafletConsumer>
                    <LeafletPM
                        position="topleft"
                        map={leafletMap}
                        error={error}
                        onEscape={this.removeLastVertex}
                    />
                    <ZoneMapPlacesSearchBox
                        onChange={handlePlaceSearch}
                        placeholder={t('zoneMap.searchLocationsAndZoneDetails')}
                        theme={theme}
                        geometries={geometries}
                        folders={folders}
                        getRadiusAndCenter={this.getRadiusAndCenter}
                        onBlur={this.enableScroll}
                        onFocus={this.disableScroll}
                        availableRiders={availableRiders}
                        availableVehicles={availableVehicles}
                    />
                    {this.getCollapseButton()}
                    {this.getZones({ geometries })}
                    {this.getMarkers({
                        geometries,
                        center: homebaseLatLng
                    })}
                </LeafletMap>
            </div>
        );
    }
}

const EnhancedMapWithZones = compose(
    connect(null, { showToast }),
    translate('entityManagement')
)(LeafletMapWithZones);

export default EnhancedMapWithZones;
